import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import useTenantStore from '../store/tenantStore';
import VerificationBanner from './VerificationBanner';

/**
 * Gates a route behind authentication and, optionally, a set of allowed roles.
 * Usage: <ProtectedRoute roles={['administrator']}><AdminDashboard /></ProtectedRoute>
 */
export default function ProtectedRoute({ roles, platformOwner = false, feature, children }) {
  const { isAuthenticated, user, hasHydrated } = useAuthStore();
  const isFeatureEnabled = useTenantStore((state) => state.isFeatureEnabled);
  const location = useLocation();

  if (!hasHydrated) {
    return <div className="min-h-screen grid place-items-center bg-gray-50"><div className="h-9 w-9 animate-spin rounded-full border-4 border-primary-100 border-t-primary-600" /></div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
  }

  if (platformOwner && !user?.is_platform_owner) {
    return <Navigate to="/dashboard" replace />;
  }

  if (feature && !isFeatureEnabled(feature)) {
    return <Navigate to="/dashboard" replace />;
  }

  if (roles && roles.length > 0) {
    const userRoles = (user?.roles || []).map((r) => r.name?.toLowerCase());
    const allowed = roles.some((r) => userRoles.includes(r.toLowerCase())) ||
      (user?.is_platform_owner && roles.some((r) => r.toLowerCase() === 'administrator'));
    if (!allowed) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return (
    <>
      <VerificationBanner />
      {children}
    </>
  );
}
